import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";

import { projectLabel } from "../../../../../../../../data/buildingPermit/fieldDatas";
import { fieldsToCheckProject } from "../../../../../../../../data/permitFormsData";

const formatNumber = (input) => {
  const digits = input.replace(/\D/g, "").slice(0, 10);
  const parts = [];

  if (digits.length > 0) parts.push(digits.slice(0, 3));
  if (digits.length > 3) parts.push(digits.slice(3, 6));
  if (digits.length > 6) parts.push(digits.slice(6));

  return parts.join("-");
};

const PROJECT_CONTACT_FIELD_NAMES = ["project_contact", "project_c_phone"];

const RenderProjectContact = ({
  full_title,
  projectContact,
  onChange,
  fields,
}) => {
  const hasValidatedOnce = useRef(false);

  const savedProjects = useSelector((state) => state.nocData?.saved_projects);
  const { addingNewProject, createDocLoading } = useSelector(
    (state) => state.buildingState,
  );

  const [projectList, setProjectList] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    setProjectList(savedProjects?.projectContact || []);
    setLoadingData(false);
  }, [savedProjects?.projectContact]);

  // Index fields by name once per render — O(1) lookups below
  const fieldMap = useMemo(
    () => Object.fromEntries((fields || []).map((f) => [f.name, f])),
    [fields],
  );

  useEffect(() => {
    // Run only if we haven’t validated AND projectList has loaded
    if (!hasValidatedOnce.current && projectList.length > 0 && projectContact) {
      const selectedId = projectContact?.value;
      const selected = projectList.find((pro) => pro.id === selectedId);

      if (!selected) {
        onChange("projectContact", "");
        PROJECT_CONTACT_FIELD_NAMES.forEach((name) => onChange(name, ""));
      } else {
        const phone = fieldMap["project_c_phone"]?.value;
        if (phone && !phone.includes("-")) {
          const formatted = formatNumber(phone);
          onChange("project_c_phone", formatted);
        }
      }

      hasValidatedOnce.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectList, projectContact]);

  const handleSelectChange = (event) => {
    const selectedId = event.target.value;
    const selected = projectList.find((p) => p.id === selectedId);

    onChange("projectContact", {
      value: selectedId,
      label: selected?.["project_contact"],
    });
    PROJECT_CONTACT_FIELD_NAMES.forEach((name) =>
      onChange(name, selected?.[name] || ""),
    );
  };

  const selectedPro = projectList?.find(
    (pro) => pro.id === projectContact?.value,
  );

  if (loadingData) {
    return <Skeleton variant="rectangular" height={120} />;
  }

  return (
    <>
      <Typography align="center" mt={2} mb={0} variant="h6">
        {full_title}
      </Typography>

      <FormControl
        fullWidth
        margin="normal"
        variant="filled"
        sx={{
          "& .MuiFilledInput-root": {
            border: projectContact ? "2px solid #004976" : undefined, // adjust thickness and color
            borderRadius: "14px",
            backgroundColor: "transparent", // optional
          },
          "& .MuiFilledInput-underline:before": {
            borderBottom: "none", // remove default bottom border
          },
          "& .MuiFilledInput-underline:after": {
            borderBottom: "none",
          },
        }}
      >
        <InputLabel>Select Project Contact</InputLabel>
        <Select
          disabled={createDocLoading || addingNewProject}
          name="projectContact"
          value={projectContact?.value || ""}
          onChange={handleSelectChange}
          MenuProps={{
            PaperProps: {
              style: {
                marginTop: "1.5px",
                maxHeight: 200,
                width: "auto",
                maxWidth: "100%",
              },
            },
          }}
        >
          <MenuItem disabled value="">
            {projectList.length === 0 ? "No records found" : "Select One"}
          </MenuItem>
          {projectList?.map((option, i) => (
            <MenuItem key={option.id + i} value={option.id}>
              {option.project_contact || "Unnamed"}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {projectContact && selectedPro && (
        <>
          {fieldsToCheckProject?.map((field, i) => (
            <TextField
              key={field + i}
              fullWidth
              disabled={!addingNewProject}
              margin="normal"
              variant="filled"
              label={projectLabel[field]}
              name={field}
              value={
                field === "project_contact"
                  ? projectContact?.label
                  : fieldMap[field]?.value || ""
              }
            />
          ))}
        </>
      )}
      <Box sx={{ border: "2px solid #d3d3d3" }} mt={1}></Box>
    </>
  );
};

export default React.memo(RenderProjectContact);
